/*
  적립 화면(`/point/view`, `/point/customer`) 공용 숫자 키패드 (2026-08-26 신설)

  왜 새로 만들었나 — 종전에는 같은 키패드가 두 곳에 서로 다른 마크업으로 복사되어 있었다
  (`components/NumberInput.js` / `page/CustomerPointPage.js` 인라인). 같은 수정을 늘 두 번 해야 했고,
  실제로 이 트랙에서 색 구분·비활성·종횡비를 두 곳에 각각 넣어야 했다.

  🔴 **정정(2026-08-27)**: 처음 이 컴포넌트를 만들 때 근거로 적었던 "`.popup-number { height:100% }`
     4개가 400% 로 넘쳐 1행만 보였다" 는 **이 저장소에서는 사실이 아니다.**
     `src/App.js:9` 의 `omoplay/dist/style.scss` 가 `.flex-col` 에 `display:flex` 를 포함시켜
     재정의하므로, 종전 마크업도 flex 컨테이너였고 4행이 `flex-shrink` 로 높이를 나눠
     **정상 렌더되고 있었다.** ⇒ 이 컴포넌트의 값어치는 "잘림 수정" 이 아니라
     **중복 제거 + 아래 ①②③ 의 반응형 보장 + 키 색 구분/비활성** 이다.

  이 컴포넌트의 레이아웃 원칙 3가지:

  ① **`grid-rows-4` = `repeat(4, minmax(0,1fr))`** 이라 높이가 아무리 줄어도 4행이 비율대로
     함께 줄어든다. `display` 를 어떤 CSS 가 어떻게 재정의하든 **그리드 트랙 수가 명시적**이라
     `height: %` 상속에 의존하지 않는다.
  ② **종횡비를 고정한다(`aspect-[5/4]` + `h-full`).** 높이에서 폭을 역산하므로 16:9·19:9 처럼
     세로가 짧은 태블릿에서도 키가 가로로 길게 **찌그러지지 않는다.** 폭이 모자라면
     `max-w-full` 이 잡아준다.
  ③ **글자 크기는 `dvh` 기반 `clamp`.** 뷰포트 높이에 따라 연속적으로 줄어들어 4:3 과 16:9
     양쪽에서 같은 비례로 보인다. (`dvh` = 모바일 주소창이 접히면 함께 줄어드는 높이)

  🔴 부모는 반드시 **높이가 확정된 컨테이너 + `min-h-0`** 이어야 한다.
     flex 자식의 `min-height` 기본값은 `auto` 라, `min-h-0` 이 없으면 내용보다 작게 줄지 못하고
     그 넘친 만큼이 **아래 형제(적립 버튼)를 화면 밖으로 밀어낸다.**
*/

const KEY_TONE = {
    // 숫자 = 기본
    digit: "bg-white text-slate-800 active:bg-slate-100",
    // '000' = 숫자이긴 하나 자릿수 입력이라 옅게 구분
    triple: "bg-slate-50 text-slate-500 active:bg-slate-100",
    // 'C' 전체 지우기 = 되돌릴 수 없는 조작이라 가장 눈에 띄게
    clear: "bg-rose-50 text-rose-600 active:bg-rose-100",
    // '←' 한 자 지우기
    back: "bg-amber-50 text-amber-600 active:bg-amber-100",
};

// 비활성 상태 = 입력이 허용되지 않는 화면(예: '고정 포인트' 채널의 적립 금액)
const KEY_DISABLED = "bg-slate-100 text-slate-300 cursor-not-allowed";

/*
  터치 입력감 (2026-08-27 — 사용자 신고 "클릭이 씹힌다")

  🔴 원인 2개. 둘 다 `click` 이벤트의 성질이다.

  ① **double-tap 대기 지연** — `public/index.html` 의 viewport 는
     `width=device-width, initial-scale=1.0` 이고 `user-scalable`/`maximum-scale` 제한이 없다.
     즉 **화면 확대가 허용**되어 있어, 브라우저는 탭이 double-tap(확대)인지 판단하려고
     `click` 을 최대 ~300ms 지연시킬 수 있다. 빠르게 두 번 누르면 두 번째가 확대 제스처로
     흡수되어 **아예 사라진다.**
     ⇒ **`touch-action: manipulation`**(Tailwind `touch-manipulation`) 으로 이 요소에서
       double-tap 확대를 끈다. 지연도 함께 사라진다. viewport 를 건드리지 않으므로
       다른 화면에는 영향이 없다.

  ② **손가락이 조금만 움직이면 `click` 이 취소된다** — 브라우저는 pointerdown 이후 이동량이
     touch slop(크롬 기준 약 8~15px)을 넘으면 탭이 아니라 스크롤/드래그로 판정해
     **`click` 을 아예 발생시키지 않는다.** 큰 키를 빠르게 누르면 손가락이 그 정도는 구른다.
     ⇒ **`onClick` 대신 `onPointerDown`** 에서 입력한다. 물리 키패드처럼 **누른 순간** 확정되므로
       이동으로 취소되는 경우가 없고 체감 반응도 한 프레임 빨라진다.
       `pointerdown` 은 터치·마우스·펜을 모두 덮는다(별도 touch 이벤트 불필요).

  🔧 함께 넣은 것
   - `transition-colors` **제거** — 눌림 색이 150ms 에 걸쳐 서서히 변해 반응이 늦어 보였다.
     이제 같은 프레임에 즉시 칠한다.
   - `-webkit-tap-highlight-color: transparent` — 안드로이드 크롬의 회색 하이라이트 잔상 제거.
   - `onContextMenu` 차단 — 길게 누를 때 뜨는 컨텍스트 메뉴가 입력을 가로채지 않게.
   - `onKeyDown`(Enter/Space) — `onClick` 을 없앴으므로 키보드 접근성을 직접 보전한다.

  ⚠️ `isPrimary` 는 **검사하지 않는다.** 손가락이 하나 걸쳐 있을 때 들어온 두 번째 탭이
     비-primary 라 무시되면 그게 바로 "씹힘" 이다. 대신 손가락마다 1회 입력된다
     (한 손가락 조작이 전제. 손바닥이 닿는 환경이면 이 판단을 재검토할 것).
*/

// 누를 때 진동 피드백. 안드로이드 크롬에서만 동작하고 iPad 는 무시된다.
// 기본 off — 케이스·거치대 환경에서 소음이 되므로 실기기에서 확인 후 켤 것.
const HAPTIC_ON_PRESS = false;

const fireHaptic = () => {
    if (!HAPTIC_ON_PRESS) return;
    try {
        navigator.vibrate?.(10);
    } catch (e) {
        /* 미지원 브라우저는 조용히 무시 */
    }
};

/**
 * @param onAppend      숫자/'000' 입력. (문자열을 뒤에 붙인다)
 * @param onBackspace   한 자 지우기
 * @param onClear       전체 지우기. `leftKey === "C"` 일 때만 쓰인다
 * @param leftKey       마지막 행 왼쪽 키. "000"(기본) 또는 "C"
 * @param disabled      전체 비활성. 값이 설정으로 고정되어 조작자가 바꿀 수 없는 경우에 쓴다
 * @param className     바깥 정렬용 추가 클래스
 */
const PointKeypad = ({ onAppend, onBackspace, onClear, leftKey = "000", disabled = false, className = "" }) => {
    const digits = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

    const keys = [
        ...digits.map((d) => ({ label: d, tone: "digit", aria: d, press: () => onAppend(d) })),
        leftKey === "C"
            ? { label: "C", tone: "clear", aria: "전체 지우기", press: onClear }
            : { label: "000", tone: "triple", aria: "000 입력", press: () => onAppend("000") },
        { label: "0", tone: "digit", aria: "0", press: () => onAppend("0") },
        { label: "←", tone: "back", aria: "한 자 지우기", press: onBackspace },
    ];

    return (
        <div className={`h-full w-full flex justify-center ${className}`}>
            <div className="h-full aspect-[5/4] max-w-full grid grid-cols-3 grid-rows-4 gap-px bg-slate-200 rounded-2xl overflow-hidden ring-1 ring-slate-200 shadow-sm select-none">
                {keys.map((k) => (
                    <button
                        key={k.label}
                        type="button"
                        aria-label={k.aria}
                        disabled={disabled}
                        /* 🔴 `onClick` 이 아니라 `onPointerDown` — 이유는 위 '터치 입력감' 주석 */
                        onPointerDown={(e) => {
                            if (disabled) return;
                            if (e.button !== 0) return;   // 마우스 우클릭·가운데클릭 무시
                            fireHaptic();
                            k.press();
                        }}
                        /* `onClick` 을 없앴으므로 키보드 조작을 직접 보전한다 */
                        onKeyDown={(e) => {
                            if (disabled) return;
                            if (e.key !== "Enter" && e.key !== " ") return;
                            e.preventDefault();
                            k.press();
                        }}
                        /* 길게 누를 때 뜨는 컨텍스트 메뉴가 입력을 가로채지 않게 */
                        onContextMenu={(e) => e.preventDefault()}
                        className={`flex items-center justify-center font-bold leading-none touch-manipulation [-webkit-tap-highlight-color:transparent] text-[clamp(1.125rem,4.4dvh,2.25rem)] ${disabled ? KEY_DISABLED : KEY_TONE[k.tone]}`}
                    >
                        {k.label}
                    </button>
                ))}
            </div>
        </div>
    );
};

export default PointKeypad;
